import { ExamCheckboxSection } from "../InitialPsychotherapy/InitialPsychotherapyConstant";

// The BIRP note reuses the checkbox-card storage shape used by the MSE /
// Psychiatric Evaluation modals: every section persists its selection as a
// comma-separated string of option names, e.g.
// { topicsAddressed: "grief,sleepHygiene" }, plus a `${key}Note` free text.

export const BIRP_THERAPEUTIC_INTERVENTIONS_SECTIONS: ExamCheckboxSection[] = [
  {
    key: "therapeuticInterventions",
    label: "Therapeutic Interventions",
    items: [
      { name: "activeListening", label: "Active Listening" },
      { name: "supportiveTherapy", label: "Supportive Therapy" },
      {
        name: "cognitiveBehavioralTherapyCbt",
        label: "Cognitive Behavioral Therapy (CBT)",
      },
      {
        name: "dialecticalBehaviorTherapyDbt",
        label: "Dialectical Behavior Therapy (DBT)",
      },
      {
        name: "acceptanceAndCommitmentTherapyAct",
        label: "Acceptance and Commitment Therapy (ACT)",
      },
      { name: "motivationalInterviewing", label: "Motivational Interviewing" },
      { name: "solutionFocusedTherapy", label: "Solution-Focused Therapy" },
      { name: "traumaInformedCare", label: "Trauma-Informed Care" },
      { name: "psychoeducation", label: "Psychoeducation" },
      { name: "mindfulnessTraining", label: "Mindfulness Training" },
      { name: "relaxationTechniques", label: "Relaxation Techniques" },
      { name: "breathingExercises", label: "Breathing Exercises" },
      {
        name: "emotionalRegulationSkills",
        label: "Emotional Regulation Skills",
      },
      { name: "distressToleranceSkills", label: "Distress Tolerance Skills" },
      { name: "cognitiveRestructuring", label: "Cognitive Restructuring" },
      { name: "behavioralActivation", label: "Behavioral Activation" },
      { name: "exposureTechniques", label: "Exposure Techniques" },
      { name: "safetyPlanning", label: "Safety Planning" },
      { name: "crisisIntervention", label: "Crisis Intervention" },
      { name: "medicationEducation", label: "Medication Education" },
      { name: "familyEducation", label: "Family Education" },
      { name: "goalSetting", label: "Goal Setting" },
      { name: "problemSolving", label: "Problem-Solving" },
      {
        name: "communicationSkillsTraining",
        label: "Communication Skills Training",
      },
      { name: "copingSkillsTraining", label: "Coping Skills Training" },
      { name: "relapsePrevention", label: "Relapse Prevention" },
    ],
  },
];

export const BIRP_TOPICS_ADDRESSED_SECTIONS: ExamCheckboxSection[] = [
  {
    key: "topicsAddressed",
    label: "Topics Addressed",
    items: [
      { name: "anxietyManagement", label: "Anxiety Management" },
      { name: "depression", label: "Depression" },
      { name: "trauma", label: "Trauma" },
      { name: "grief", label: "Grief" },
      { name: "stressManagement", label: "Stress Management" },
      { name: "relationships", label: "Relationships" },
      { name: "familyConflict", label: "Family Conflict" },
      { name: "workConcerns", label: "Work Concerns" },
      { name: "schoolConcerns", label: "School Concerns" },
      { name: "medicationCompliance", label: "Medication Compliance" },
      { name: "lifestyleModifications", label: "Lifestyle Modifications" },
      { name: "sleepHygiene", label: "Sleep Hygiene" },
      { name: "substanceUse", label: "Substance Use" },
      { name: "selfCare", label: "Self-Care" },
    ],
  },
];

export const BIRP_PATIENT_PARTICIPATION_SECTIONS: ExamCheckboxSection[] = [
  {
    key: "patientParticipation",
    label: "Patient Participation",
    items: [
      { name: "fullyEngaged", label: "Fully Engaged" },
      { name: "cooperative", label: "Cooperative" },
      { name: "activelyParticipated", label: "Actively Participated" },
      { name: "minimalParticipation", label: "Minimal Participation" },
      { name: "resistant", label: "Resistant" },
      { name: "requiredPrompting", label: "Required Prompting" },
      { name: "openToFeedback", label: "Open to Feedback" },
      { name: "demonstratedInsight", label: "Demonstrated Insight" },
    ],
  },
];

export const BIRP_RESPONSE_TO_INTERVENTION_SECTIONS: ExamCheckboxSection[] = [
  {
    key: "responseToIntervention",
    label: "Response to Intervention",
    items: [
      {
        name: "demonstratedUnderstanding",
        label: "Demonstrated Understanding",
      },
      { name: "practicedNewSkills", label: "Practiced New Skills" },
      { name: "identifiedTriggers", label: "Identified Triggers" },
      { name: "completedExercises", label: "Completed Exercises" },
      {
        name: "expressedEmotionsAppropriately",
        label: "Expressed Emotions Appropriately",
      },
      {
        name: "reportedSymptomImprovement",
        label: "Reported Symptom Improvement",
      },
      {
        name: "reportedOngoingDifficulties",
        label: "Reported Ongoing Difficulties",
      },
      { name: "askedRelevantQuestions", label: "Asked Relevant Questions" },
      {
        name: "verbalizedCopingStrategies",
        label: "Verbalized Coping Strategies",
      },
      { name: "acceptedRecommendations", label: "Accepted Recommendations" },
    ],
  },
];

// Response → Progress is a single select, so it is stored as one option name in
// `progress` rather than as a comma-separated string.
export const BIRP_PROGRESS_OPTIONS = [
  { value: "significantProgress", label: "Significant Progress" },
  { value: "moderateProgress", label: "Moderate Progress" },
  { value: "minimalProgress", label: "Minimal Progress" },
  { value: "noSignificantChange", label: "No Significant Change" },
] as const;

export const BIRP_PROGRESS_FIELDS = ["progress"];

// Fields each modal owns — passed to useFormDraft so edits only reach the note
// on Save, exactly like the MSE modal.
const sectionFields = (sections: ExamCheckboxSection[]) =>
  sections.flatMap((section) => [
    section.key,
    section.noteKey ?? `${section.key}Note`,
  ]);

export const BIRP_THERAPEUTIC_INTERVENTIONS_FIELDS = sectionFields(
  BIRP_THERAPEUTIC_INTERVENTIONS_SECTIONS
);

export const BIRP_TOPICS_ADDRESSED_FIELDS = sectionFields(
  BIRP_TOPICS_ADDRESSED_SECTIONS
);

export const BIRP_PATIENT_PARTICIPATION_FIELDS = sectionFields(
  BIRP_PATIENT_PARTICIPATION_SECTIONS
);

export const BIRP_RESPONSE_TO_INTERVENTION_FIELDS = sectionFields(
  BIRP_RESPONSE_TO_INTERVENTION_SECTIONS
);
